"use client"

import React, { useState, useEffect, ReactNode, HTMLAttributes } from "react"
import type { MotionProps as FramerMotionProps } from "framer-motion"

// Lazy load framer-motion module
let motionModule: typeof import("framer-motion") | null = null
let motionLoadPromise: Promise<typeof import("framer-motion")> | null = null

const loadMotion = () => {
  if (motionModule) return Promise.resolve(motionModule)
  if (motionLoadPromise) return motionLoadPromise
  
  motionLoadPromise = import("framer-motion")
  motionLoadPromise.then((mod) => {
    motionModule = mod
  })
  return motionLoadPromise
}

type HTMLTag = keyof React.JSX.IntrinsicElements

type BaseMotionProps = Pick<FramerMotionProps, "initial" | "animate" | "whileInView" | "viewport" | "variants">

type MotionProps = BaseMotionProps & HTMLAttributes<HTMLElement> & {
  as?: HTMLTag
  children?: ReactNode
}

export function Motion({ as = "div", children, ...props }: MotionProps) {
  const [motionLoaded, setMotionLoaded] = useState(false)
  const [MotionComponent, setMotionComponent] = useState<React.ComponentType<Record<string, unknown>> | null>(null)
  
  useEffect(() => {
    // Load framer-motion after initial render and a short delay to reduce TBT
    // This allows the page to render first, then load animations
    const timer = setTimeout(() => {
      loadMotion().then((mod) => {
        const element = as as keyof typeof mod.motion
        if (mod.motion[element]) {
          setMotionComponent(() => mod.motion[element] as React.ComponentType<Record<string, unknown>>)
          setMotionLoaded(true)
        }
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [as])
  
  // Extract framer-motion specific props
  const { initial, animate, whileInView, viewport, variants, ...htmlProps } = props
  
  // For SSR and initial render, use regular HTML element (no animations)
  if (!motionLoaded || !MotionComponent) {
    return React.createElement(as, htmlProps as React.HTMLAttributes<HTMLElement>, children)
  }

  return (
    <MotionComponent 
      initial={initial}
      animate={animate}
      whileInView={whileInView}
      viewport={viewport}
      variants={variants}
      {...(htmlProps as Record<string, unknown>)}
    >
      {children}
    </MotionComponent>
  )
}
